import React, { useEffect, useCallback } from 'react';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { getChangeRequestAvailableParticipants } from '@/services/change-requests';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, SelectionList } from '@ygtechnologies/rubus-ui';
import { ChangeRequestAvailableParticipant, ChangeRequestParticipantInfo } from '@/types';
import theme from './ChangeRequestParticipantSelectList.module.scss';
import { UserIcon } from '@/components/Icons';
import { useAsyncState } from '@/utils/hooks';
import { Trans } from '@lingui/react/macro';

//TODO: make this not change request specific

export interface ChangeRequestParticipantSelectListItem {
    userId: number;
    profileId: number;
}

interface ChangeRequestParticipantSelectListProps {
    projectId: number;
    onChange(participants: ChangeRequestParticipantSelectListItem[]): void;
    value: ChangeRequestParticipantSelectListItem[];
    forceCurrentUser?: boolean;
    participantsIdsWithOffer?: number[] | null;
}

function itemCompare(x: ChangeRequestParticipantSelectListItem, y: ChangeRequestParticipantSelectListItem) {
    return x.userId === y.userId && x.profileId === y.profileId;
}

const ChangeRequestParticipantSelectList: React.FC<ChangeRequestParticipantSelectListProps> = ({
    projectId,
    onChange,
    value,
    forceCurrentUser = false,
    participantsIdsWithOffer,
}) => {
    const [availableParticipants] = useAsyncState(() => getChangeRequestAvailableParticipants(projectId), [projectId], null);

    const handleParticipantAdd = useCallback(
        (participant: ChangeRequestAvailableParticipant) => {
            if (value!.some((x) => itemCompare(x, participant))) {
                return;
            }

            const newValue = [...value!, { userId: participant.userId, profileId: participant.profileId }];

            triggerHandler(onChange, newValue);
        },
        [value, onChange]
    );

    useEffect(() => {
        if (forceCurrentUser && availableParticipants) {
            const currentUser = availableParticipants.find((x) => x.isCurrentUser);

            if (currentUser) {
                handleParticipantAdd(currentUser);
            }
        }
    }, [handleParticipantAdd, forceCurrentUser, availableParticipants]);

    const handleParticipantRemove = (participant: ChangeRequestAvailableParticipant) => {
        const newValue = value.filter((x) => !itemCompare(x, participant));

        if (forceCurrentUser && participant.isCurrentUser) {
            return;
        }

        triggerHandler(onChange, newValue);
    };

    if (!availableParticipants) {
        return <LoadingIndicator />;
    }

    return (
        <SelectionList>
            {availableParticipants.map((ap) => {
                const isSelected = value.some((x) => itemCompare(x, ap));
                const showOfferWarning = !isSelected && participantsIdsWithOffer?.some((x) => x === ap.userId);
                return (
                    <SelectionList.Item
                        key={ap.userId}
                        selected={isSelected}
                        onChange={(checked) => (checked ? handleParticipantAdd(ap) : handleParticipantRemove(ap))}
                        label={
                            <>
                                <div className={theme.itemContent}>
                                    <UserIcon userId={ap.userId} sizeInPx={36} />
                                    <span className={theme.name}>{ap.displayName}</span>
                                    <small>{ap.profileName}</small>
                                </div>
                                {showOfferWarning && (
                                    <AlertBanner stretch type="warning">
                                        <Trans>
                                            <strong>Please note</strong> removing this participant, will mean that their
                                            offer will be removed as well.
                                        </Trans>
                                    </AlertBanner>
                                )}
                            </>
                        }
                    />
                );
            })}
        </SelectionList>
    );
};

export default ChangeRequestParticipantSelectList;
